import Vue from 'vue';
import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFilteredConversationView,
} from './helpers/actionHelpers';
import { buildConversationContextKey } from 'dashboard/helper/conversationContext';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import messageReadActions from './actions/messageReadActions';
import messageForwardActions from './actions/messageForwardActions';
import * as Sentry from '@sentry/browser';

let activeConversationListRequest = null;

// Sequencia da ultima requisicao de LISTA -- filtrada ou nao.
//
// `activeConversationListRequest` so enxerga `fetchAllConversations`: a busca
// filtrada nunca se registrou nele, entao nenhum dos dois lados via o outro.
// Sem isso, uma requisicao ja superada podia marcar a falha depois que a nova
// comecou, e a marca ficava: lista carregada certinho, com a faixa "nao foi
// possivel carregar" no rodape e o scroll infinito desligado.
//
// Um contador basta -- so interessa saber quem foi o ultimo a pedir.
let listRequestSequence = 0;

const startListRequest = () => {
  listRequestSequence += 1;

  return listRequestSequence;
};

const isCurrentListRequest = token => token === listRequestSequence;

const isRequestCanceled = error => {
  return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
};

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// Insere a conversa quando ela ainda nao esta no store. Existe porque
// `UPDATE_CONVERSATION` deixou de inserir por conta propria: quem sabe se a
// conversa pertence a lista aberta e a action, nao a mutation.
const insertConversationIfMissing = ({ commit, state }, conversation) => {
  const isKnown = state.allConversations.some(c => c.id === conversation.id);
  if (!isKnown) {
    commit(types.ADD_CONVERSATION, conversation);
  }
};

// actions
const actions = {
  getConversation: async ({ commit, state }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      // Abrir uma conversa por link direto e um gesto do usuario: ela precisa
      // entrar no store mesmo que a lista atual nao a contenha, senao a tela
      // de conversa nao tem o que renderizar. Antes isso vinha de graca do
      // `else { push }` dentro de `UPDATE_CONVERSATION`; agora que a mutation
      // so atualiza, a insercao e explicita aqui.
      insertConversationIfMissing({ commit, state }, response.data);
      commit(types.UPDATE_CONVERSATION, response.data);
      // `EDIT_CONTACT` pelo mesmo motivo de `setContact`: `SET_CONTACT_ITEM`
      // empurraria o remetente para `sortOrder`, a lista renderizada em
      // Contatos. E este caminho e alcancado ao vivo -- `addMessage` chama
      // `getConversation` quando a mensagem e de uma conversa fora do store --,
      // entao, com a tela de Contatos aberta, o contato de cada mensagem
      // recebida brotava na listagem, fora do filtro e da pagina.
      commit(`contacts/${types.EDIT_CONTACT}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    const requestToken = startListRequest();
    let controller = null;
    // Esta invocacao ainda e a dona da flag de carregamento?
    //
    // Toda troca de filtro aborta a request anterior e assume o lugar dela em
    // `activeConversationListRequest`. Quando a abortada acorda no catch, quem
    // manda ja e a nova -- e desarmar o loader em nome dela destravava o
    // IntersectionObserver da lista sobre um resultado vazio: ele disparava
    // `loadMoreConversations`, que abortava a request boa, cujo catch desarmava
    // de novo. Ciclo de ~1 frame, muito abaixo de um round-trip: nenhuma
    // resposta chegava, a lista ficava vazia e os contadores caiam no default.
    //
    // A leitura acontece depois do await, entao o valor e sempre o do momento
    // do desfecho, nao o da largada.
    const ownsLoadingStatus = () =>
      activeConversationListRequest?.controller === controller;
    try {
      const params = state.conversationFilters;
      const requestContextKey = buildConversationContextKey(params);
      // Always abort the in-flight request so the most recent filter click wins.
      // The post-flight guard below still validates the contextKey, but aborting
      // prevents two concurrent responses from racing for the same filter set.
      if (
        activeConversationListRequest &&
        activeConversationListRequest.controller
      ) {
        activeConversationListRequest.controller.abort();
      }

      controller = new AbortController();
      activeConversationListRequest = {
        contextKey: requestContextKey,
        controller,
      };

      const {
        data: { data },
      } = await ConversationApi.get({
        ...params,
        signal: controller.signal,
      });
      // Ha duas maneiras de ficar para tras: o filtro mudou embaixo desta
      // request (contextKey) ou outra busca de lista pediu depois (sequencia).
      // A segunda inclui a busca filtrada, que nao se registra em
      // `activeConversationListRequest` -- sair de uma pasta para a lista comum
      // caia exatamente aqui.
      const isSuperseded = !isCurrentListRequest(requestToken);
      const isStaleContext =
        requestContextKey !==
        buildConversationContextKey(state.conversationFilters);
      if (isSuperseded || isStaleContext) {
        // Resposta obsoleta: descartada sem commit. Se nenhuma request mais nova
        // assumiu, a flag e nossa e ninguem mais vai desarma-la -- mas se
        // assumiu, o loader e dela: desarmar aqui apagaria o spinner de uma
        // busca que ainda esta em voo.
        if (!isSuperseded && ownsLoadingStatus()) {
          commit(types.CLEAR_LIST_LOADING_STATUS);
        }
        return;
      }
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Erro real (rede, 500) tambem precisa desarmar: so o caminho de sucesso
      // limpa a flag, dentro de `buildConversationList`. Sem isso um erro na
      // ultima request deixava o spinner para sempre.
      //
      // Mesma ressalva do desfecho obsoleto: se outra busca de lista pediu
      // depois desta, o spinner na tela e o dela.
      if (isCurrentListRequest(requestToken) && ownsLoadingStatus()) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
      }
      if (isRequestCanceled(error)) {
        return;
      }
      // Desarmar o loader sozinho trocaria um defeito por outro: com a lista
      // vazia o IntersectionObserver do scroll infinito volta a montar, dispara
      // sobre um alvo ja visivel e pede a proxima pagina -- que falha de novo.
      // Enquanto o backend estiver fora, o painel martelaria /conversations sem
      // parar. A marca de falha segura o scroll infinito ate que um gesto do
      // usuario (trocar filtro, aba ou pasta) comece uma tentativa nova, que e
      // quem zera a marca.
      // A marca so vale se ninguem mais pediu depois: uma resposta atrasada
      // nao pode falar em nome da lista que esta na tela agora.
      if (isCurrentListRequest(requestToken)) {
        commit(types.SET_LIST_FETCH_FAILED, true);
      }
    } finally {
      if (
        activeConversationListRequest &&
        activeConversationListRequest.controller === controller
      ) {
        activeConversationListRequest = null;
      }
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    const requestToken = startListRequest();
    try {
      const { data } = await ConversationApi.filter(params);
      // Resposta superada nao escreve na lista.
      //
      // A guarda de falha ja impedia uma resposta atrasada de marcar erro; a
      // ordem inversa continuava aberta. `buildConversationList` escreve a
      // lista, desarma o loader e apaga a marca de falha -- entao um sucesso
      // obsoleto devolvia a lista da pasta antiga por cima da nova e escondia o
      // erro da requisicao que esta valendo, com o scroll infinito rodando
      // sobre o resultado errado. Esta busca nao tem AbortController, entao a
      // sequencia e a unica coisa que sabe quem pediu por ultimo.
      if (isCurrentListRequest(requestToken)) {
        buildConversationList(
          { commit, dispatch },
          params,
          data,
          'appliedFilters'
        );
      }
      return { data }; // Retornar os dados para que o componente possa acessá-los
    } catch (error) {
      // Mesmo desfecho que `fetchAllConversations` da a uma falha real.
      //
      // So o caminho de sucesso desarmava o loader, dentro de
      // `buildConversationList`, e o catch apenas relancava. Com uma pasta ou
      // filtro ativo, uma falha deixava o spinner do rodape girando para
      // sempre: nem o bloco de falha nem o `404` chegavam a aparecer, e o botao
      // de tentar novamente -- escrito justamente para estes contextos -- nao
      // tinha como ser renderizado.
      if (isCurrentListRequest(requestToken)) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
        commit(types.SET_LIST_FETCH_FAILED, true);
      }
      throw error;
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = null;

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);
    if (data.dataFetched === undefined) {
      try {
        // When the conversation came from an endpoint that does not embed the
        // last message (eg. conversations#show), `messages` is empty. Fetching
        // without `before` returns the latest page instead of leaving the chat
        // stuck on the "no messages" empty state.
        await dispatch('fetchPreviousMessages', {
          after,
          before: data.messages?.length ? data.messages[0].id : undefined,
          conversationId: data.id,
        });
        Vue.set(data, 'dataFetched', true);
      } catch (error) {
        // Ignore error
      }
    }
  },

  assignAgent: async ({ dispatch, state }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', {
        assignee: response.data,
        conversationId,
      });
      emitter.emit(BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED, {
        scope: 'card',
        entityType: 'conversation',
        cardId: conversationId,
        labelTitles:
          state.allConversations.find(
            conversation => conversation.id === Number(conversationId)
          )?.labels || [],
        updatedData: {
          assignee_id: response.data?.id || null,
          assignee_name: response.data?.name || null,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  setCurrentChatAssignee({ commit }, payload) {
    commit(types.ASSIGN_AGENT, payload);
  },

  assignTeam: async ({ dispatch, state }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
      emitter.emit(BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED, {
        scope: 'card',
        entityType: 'conversation',
        cardId: conversationId,
        labelTitles:
          state.allConversations.find(
            conversation => conversation.id === Number(conversationId)
          )?.labels || [],
        updatedData: {
          team_id: response.data?.id || null,
          team_name: response.data?.name || null,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit, state },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
      emitter.emit(BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED, {
        scope: 'card',
        entityType: 'conversation',
        cardId: conversationId,
        labelTitles:
          state.allConversations.find(
            conversation => conversation.id === Number(conversationId)
          )?.labels || [],
        updatedData: {
          status: updatedStatus,
        },
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });

      // Map pendingMessage properties to MessageApi.create expected format
      const messagePayload = {
        conversationId: conversationId || pendingMessage.conversationId,
        message: pendingMessage.content || pendingMessage.message,
        private: pendingMessage.private,
        contentAttributes:
          pendingMessage.content_attributes || pendingMessage.contentAttributes,
        echo_id: pendingMessage.echo_id || pendingMessage.echoId,
        files: pendingMessage.files,
        ccEmails: pendingMessage.cc_emails || pendingMessage.ccEmails || '',
        bccEmails: pendingMessage.bcc_emails || pendingMessage.bccEmails || '',
        toEmails: pendingMessage.to_emails || pendingMessage.toEmails || '',
        mailSubject:
          pendingMessage.mail_subject || pendingMessage.mailSubject || '',
        templateParams:
          pendingMessage.templateParams || pendingMessage.template_params,
        // Marca de qual mensagem leva a assinatura. O servidor e quem monta a
        // assinatura, no envio; aqui so passamos a marca adiante.
        applySignature:
          pendingMessage.applySignature ||
          pendingMessage.apply_signature ||
          false,
      };

      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(messagePayload);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  updateMessage({ commit }, message) {
    commit('ADD_MESSAGE', message);
  },

  votePoll(_, { conversationId, messageId, selectedOptions }) {
    return MessageApi.vote(conversationId, messageId, selectedOptions);
  },

  addMessage({ commit, state, dispatch, rootState }, message) {
    const { conversation_id: conversationId } = message;
    const isConversationExist = state.allConversations.find(
      c => c.id === conversationId
    );

    // Mensagem de conversa que nao esta no store: buscar a conversa a insere
    // na lista aberta. Isso e o comportamento certo na lista crua (a conversa
    // acabou de ter atividade e sobe para o topo), mas numa pasta, filtro
    // salvo, mencoes ou participando ela entraria sem passar por filtro
    // nenhum. A guarda e a mesma de `addConversation`.
    if (
      !isConversationExist &&
      !isOnFilteredConversationView(state, rootState)
    ) {
      dispatch('getConversation', conversationId);
    }

    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt: message.created_at,
      conversationId: message.conversation_id,
    });
  },

  deleteMessage: async function deleteMessage(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  editMessage: async function editMessage(
    { commit },
    { conversationId, messageId, content, attachments, attachmentsToRemove }
  ) {
    try {
      const { data } = await MessageApi.updateMessage(
        conversationId,
        messageId,
        {
          content,
          attachments,
          attachmentsToRemove,
        }
      );
      commit(types.ADD_MESSAGE, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteAttachment: async function deleteAttachment(
    { commit },
    { conversationId, attachmentId }
  ) {
    try {
      await MessageApi.deleteAttachment(conversationId, attachmentId);
    } catch (error) {
      throw new Error(error);
    }
  },

  toggleReaction: async function toggleReaction(
    { commit },
    { conversationId, messageId, emoji }
  ) {
    try {
      const { data } = await MessageApi.toggleReaction(
        conversationId,
        messageId,
        emoji
      );
      commit(types.ADD_MESSAGE, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    // `isOnUnattendedView` explicito, fora do helper: nao-atendidas ficou de
    // fora dele porque `filterByUnattended` recorta a aba, mas esta guarda
    // aqui e mais antiga que este fix e mexer nela e outro assunto -- o
    // comportamento de `conversation.created` na aba de nao-atendidas fica
    // exatamente como estava.
    if (
      !isOnFilteredConversationView(state, rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  // Mencoes e nao-atendidas tem evento proprio: quando ele chega, a conversa
  // PERTENCE aquela lista, entao aqui a insercao e legitima. Ela ia junto no
  // `else { push }` da mutation; com a mutation so atualizando, precisa ser
  // dita.
  addMentions({ commit, state, dispatch, rootState }, conversation) {
    if (!isOnMentionsView(rootState)) return;
    insertConversationIfMissing({ commit, state }, conversation);
    dispatch('updateConversation', conversation);
  },

  // ATENCAO: nenhum handler despacha `addUnattended` hoje -- diferente de
  // `addMentions`, que sai de `onConversationMentioned`. Ela e mantida
  // coerente com a mutation que deixou de inserir sozinha, mas nao cobre
  // caminho vivo nenhum; quem faz a aba de nao-atendidas receber conversa ao
  // vivo e o `addMessage` (por isso nao-atendidas ficou fora do helper).
  addUnattended({ commit, state, dispatch, rootState }, conversation) {
    if (!isOnUnattendedView(rootState)) return;
    insertConversationIfMissing({ commit, state }, conversation);
    dispatch('updateConversation', conversation);
  },

  // `UPDATE_CONVERSATION` so atualiza, entao a insercao mora aqui -- com a
  // guarda que faltava antes.
  //
  // Na lista crua a insercao e CERTA: se outro agente reabre uma conversa
  // resolvida que este agente nunca carregou, o `conversation.status_changed`
  // e o unico aviso que a aba "Abertas" tem de que ela agora pertence ali, e
  // `applyPageFilters` (status, inbox, time, label) mais os getters de
  // responsavel seguram o que nao casa. Sem inserir, o contador da aba --
  // que `fetchConversationStats` atualiza logo em seguida, no mesmo handler
  // -- passava a discordar da lista ate o F5.
  updateConversation({ commit, state, dispatch, rootState }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    if (!isOnFilteredConversationView(state, rootState)) {
      insertConversationIfMissing({ commit, state }, conversation);
    }
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      // `EDIT_CONTACT` e nao `SET_CONTACT_ITEM`: os dois atualizam
      // `records`, mas o segundo tambem faz `sortOrder.push` -- e `sortOrder`
      // E a lista de contatos renderizada. Este caminho vem de socket
      // (`onConversationContactChange`), entao o push anexava a listagem
      // aberta um contato que o filtro atual nao devolveria.
      commit(`contacts/${types.EDIT_CONTACT}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit, state },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
      emitter.emit(BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED, {
        scope: 'card',
        entityType: 'conversation',
        cardId: conversationId,
        labelTitles:
          state.allConversations.find(
            conversation => conversation.id === Number(conversationId)
          )?.labels || [],
        updatedData: {
          custom_attributes,
        },
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignFranchiseUnit: async (
    { commit },
    { conversationId, franchiseUnitId }
  ) => {
    const response = await ConversationApi.assignFranchiseUnit({
      conversationId,
      franchiseUnitId,
    });
    commit(types.UPDATE_CONVERSATION, response.data);
    return response.data;
  },

  assignPriority: async ({ dispatch, state }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
      emitter.emit(BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED, {
        scope: 'card',
        entityType: 'conversation',
        cardId: conversationId,
        labelTitles:
          state.allConversations.find(
            conversation => conversation.id === Number(conversationId)
          )?.labels || [],
        updatedData: {
          priority,
        },
      });
    } catch (error) {
      // O erro SOBE. Quem chama grava a prioridade localmente antes de a API
      // responder (escrita otimista) e precisa saber que a chamada falhou para
      // desfazer -- engolir aqui fazia a tela anunciar sucesso e continuar
      // exibindo uma prioridade que o servidor recusou. Os tres chamadores
      // (ConversationAction, ChatList e o atalho de teclado) tratam.
      throw error;
    }
  },

  // Escrita otimista, com o erro subindo como no assignPriority: quem chama
  // mostra a mensagem (o 422 do limite de fixadas tem texto proprio) e o
  // estado local ja voltou ao que era.
  togglePin: async ({ commit, state }, { conversationId, pinned }) => {
    const previous = !!state.allConversations.find(
      c => c.id === Number(conversationId)
    )?.pinned;
    commit(types.SET_CONVERSATION_PINNED, { conversationId, pinned });
    try {
      if (pinned) {
        await ConversationApi.pin(conversationId);
      } else {
        await ConversationApi.unpin(conversationId);
      }
    } catch (error) {
      commit(types.SET_CONVERSATION_PINNED, {
        conversationId,
        pinned: previous,
      });
      throw error;
    }
  },

  // Em massa (#1316) NAO e otimista: o servidor e tudo ou nada, entao so depois do
  // 200 as conversas viram fixadas (ou nao). Em erro nada muda e ele sobe para a
  // tela mostrar a mensagem do servidor (422 do limite de fixadas).
  bulkTogglePin: async ({ commit }, { conversationIds, pinned }) => {
    if (pinned) {
      await ConversationApi.bulkPin(conversationIds);
    } else {
      await ConversationApi.bulkUnpin(conversationIds);
    }
    conversationIds.forEach(conversationId =>
      commit(types.SET_CONVERSATION_PINNED, { conversationId, pinned })
    );
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  ...messageReadActions,
  ...messageForwardActions,
};

export default actions;
